// Витрина (index): категории, карточки тарифов, поиск, модалка, корзина.
import { ApiError, api, type Plan } from './api';
import { renderBanners } from './banners';
import { config, loadConfig, priceMarkup } from './price';
import { applyStaticI18n, setLang, t } from './i18n';
import { applyMarketTheme, initTheme } from './theme';
import {
  addToCart,
  getCart,
  inCart,
  markWelcomeSeen,
  toggleCartItem,
  welcomeSeen,
  type CartItem,
} from './store';
import {
  enterFullscreen,
  hideBackButton,
  isDesktopOrWeb,
  showMobileOnly,
  tenantSlug,
  tg,
  withCart,
} from './tg';
import { $, attr, esc, lockBgScroll, makeSheetDraggable, unlockBgScroll } from './ui';
import { renderIcons } from './icons';

const ALL = '__all__';
// Баннеры временно выключены по просьбе владельца. Вернуть — поставить true.
const SHOW_BANNERS = false;

let plans: Plan[] = [];

// Фильтры держим в состоянии, а не в style.display карточек. Раньше поиск и
// категории независимо перезаписывали видимость друг друга: выбрал категорию,
// начал искать — и категория «отваливалась», а очистка поиска её не возвращала.
let activeCategory = ALL;
let searchQuery = '';

function toCartItem(p: Plan): CartItem {
  return { id: p.id, name: p.name, price: p.price, category: p.category ?? undefined };
}

// --- категории -------------------------------------------------------------

interface CategoryChip {
  name: string;
  image: string | null;
}

function categories(): CategoryChip[] {
  const seen: CategoryChip[] = [];
  for (const p of plans) {
    const c = p.category ?? '';
    if (c && !seen.some((s) => s.name === c)) {
      seen.push({ name: c, image: p.category_image ?? null });
    }
  }
  return seen;
}

/** Монограмма-фолбэк для категории без картинки — первая буква имени. */
function monogram(label: string): string {
  return (label.trim()[0] || '?').toUpperCase();
}

function renderCategories(): void {
  const bar = $('rate-bar');
  const box = $('category-container');
  const cats = categories();
  if (!bar || !box) return;
  if (!cats.length) {
    bar.style.display = 'none';
    return;
  }
  bar.style.display = '';
  box.innerHTML = '';

  // Плитка = круглая иконка сверху + подпись снизу (референс владельца). Картинки
  // нет → монограмма. Акцент — только у выбранной плитки (рамка/подпись/кольцо).
  const mkBtn = (label: string, keyword: string, image: string | null): HTMLButtonElement => {
    const btn = document.createElement('button');
    btn.className = 'rate-item';
    btn.dataset.category = keyword;

    const icon = document.createElement('span');
    icon.className = 'rate-item__icon';
    if (image) {
      const img = document.createElement('img');
      img.src = image;
      img.alt = '';
      img.loading = 'lazy';
      // Битый URL — не оставляем пустой кружок, падаем на монограмму.
      img.onerror = (): void => {
        icon.classList.add('rate-item__icon--mono');
        icon.textContent = monogram(label);
      };
      icon.appendChild(img);
    } else {
      icon.classList.add('rate-item__icon--mono');
      icon.textContent = monogram(label);
    }

    const text = document.createElement('span');
    text.className = 'rate-item__label';
    text.textContent = label;

    btn.append(icon, text);
    return btn;
  };

  // Кнопку «Все» можно скрыть в панели — тогда витрина открывается на первой
  // категории, общего списка нет. У неё нет категории → всегда монограмма.
  if (config().show_all_category) {
    box.appendChild(mkBtn(config().all_label || 'Все', ALL, null));
  } else if (activeCategory === ALL) {
    activeCategory = cats[0].name;
  }
  cats.forEach((c) => box.appendChild(mkBtn(c.name, c.name, c.image)));
  // До 5 плиток включительно — делим ширину поровну, без прокрутки (владелец
  // хочет, чтобы все категории влезали разом). Больше — лента с прокруткой.
  box.classList.toggle('rate-scroll--fill', box.childElementCount <= 5);
  applyFilters();
}

/** Единственное место, которое решает, какие карточки видно. */
function applyFilters(): void {
  document.querySelectorAll<HTMLElement>('.rate-item').forEach((btn) => {
    btn.classList.toggle('active', (btn.dataset.category || ALL) === activeCategory);
  });

  const q = searchQuery.trim().toLowerCase();
  let visible = 0;
  document.querySelectorAll<HTMLElement>('.product-item').forEach((item) => {
    const cell = item.closest<HTMLElement>('.col-6') ?? item.parentElement;
    if (!cell) return;
    const byCategory = activeCategory === ALL || (item.dataset.category || '') === activeCategory;
    const byQuery = !q || (item.dataset.name || '').toLowerCase().includes(q);
    const show = byCategory && byQuery;
    cell.style.display = show ? '' : 'none';
    if (show) visible++;
  });

  const empty = $('grid-empty');
  if (empty) empty.style.display = visible ? 'none' : '';
}

// --- карточки --------------------------------------------------------------

function cardHtml(p: Plan): string {
  const img = p.image_url
    ? `<img src="${attr(p.image_url)}" alt="${attr(p.name)}" loading="lazy" onerror="this.remove()">`
    : `<div class="thumb-fallback">${esc(p.name)}</div>`;
  return `<div class="col-6">
    <article class="product-item" data-id="${p.id}" data-name="${attr(p.name)}" data-category="${attr(p.category ?? '')}">
      <div class="product-thumb" data-details>
        ${img}
        <div class="price-overlay">${priceMarkup(p.price)}</div>
      </div>
      <div class="product-info">
        <h3>${esc(p.name)}</h3>
        <div class="card-price">${priceMarkup(p.price)}</div>
        <div class="product-actions">
          <button class="btn-buy" data-buy>${t('buy')}</button>
          <button class="btn-cart ${inCart(p.id) ? 'button-added' : ''}" data-cart aria-label="${t('to_cart')}">
            <i data-lucide="shopping-cart" aria-hidden="true"></i>
          </button>
        </div>
      </div>
    </article>
  </div>`;
}

function renderGrid(): void {
  const grid = $('product-grid');
  if (!grid) return;
  if (!plans.length) {
    grid.innerHTML = `<div class="infotext"><div class="info-text">${t('not_found')}</div></div>`;
    return;
  }
  grid.innerHTML =
    `<div class="grid">${plans.map(cardHtml).join('')}</div>` +
    '<div class="infotext" id="grid-empty" style="display:none">' +
    `<div class="info-text">${t('search_empty')}</div></div>`;
  renderIcons(grid); // заглушки data-lucide в карточках → настоящие svg
}

// --- корзина ---------------------------------------------------------------

function syncCartButtons(): void {
  document.querySelectorAll<HTMLElement>('.product-item').forEach((item) => {
    const id = Number(item.dataset.id);
    item.querySelector('.btn-cart')?.classList.toggle('button-added', inCart(id));
  });
}

function updateCartFooter(): void {
  const footer = $('cart-footer');
  const count = $('cart-count');
  if (!footer || !count) return;
  const cart = getCart();
  footer.classList.toggle('d-none', !cart.length);
  count.textContent = String(cart.length);
}

/** Промокод из уведомления (?promo_code=…) — помним на сессию и доносим до корзины. */
function pendingPromo(): string {
  const k = `promo:${tenantSlug()}`;
  const fromUrl = new URLSearchParams(location.search).get('promo_code');
  if (fromUrl) {
    try {
      sessionStorage.setItem(k, fromUrl);
    } catch {
      /* приватный режим */
    }
    return fromUrl;
  }
  try {
    return sessionStorage.getItem(k) || '';
  } catch {
    return '';
  }
}

function openCart(extra = ''): void {
  // Состав кладём в адрес: хранилище переход между страницами переживает не в
  // каждом вебвью, а ссылка доезжает всегда. Промокод (если пришёл из уведомления)
  // доносим тем же адресом — на корзине он применится сам.
  const promo = pendingPromo();
  let path = `/cart.html${extra}`;
  if (promo) path += `${path.includes('?') ? '&' : '?'}promo_code=${encodeURIComponent(promo)}`;
  location.href = withCart(path, getCart().map((it) => it.id));
}

function buyNow(p: Plan): void {
  addToCart(toCartItem(p));
  openCart('?pay=1');
}

// --- модалка деталей -------------------------------------------------------

let modalPlan: Plan | null = null;

function openDetail(p: Plan): void {
  modalPlan = p;
  const img = $('modal-image') as HTMLImageElement | null;
  if (img) {
    if (p.image_url) {
      img.src = p.image_url;
      img.style.display = '';
    } else {
      img.style.display = 'none';
    }
  }
  const nameEl = $('modal-name');
  if (nameEl) nameEl.textContent = p.name;

  // «Что внутри» — списком перед сплошным описанием: его читают, описание чаще нет.
  const hlEl = $('modal-highlights');
  if (hlEl) {
    hlEl.innerHTML = p.highlights.length
      ? `<ul class="highlights">${p.highlights.map((h) => `<li>${esc(h)}</li>`).join('')}</ul>`
      : '';
  }
  const descEl = $('modal-description');
  if (descEl) descEl.textContent = p.description || '';
  const priceEl = $('modal-price');
  if (priceEl) priceEl.innerHTML = priceMarkup(p.price);

  const modal = $('modal');
  if (!modal) return;
  modal.style.display = 'block';
  requestAnimationFrame(() => modal.classList.add('show'));
  lockBgScroll();
  makeSheetDraggable(
    modal.querySelector<HTMLElement>('.modal-content'),
    closeModal,
    modal.querySelector('.modal-body'),
  );
}

/** Открыть карточку тарифа по id — точка входа для баннеров. */
function openPlanById(planId: number): void {
  const p = plans.find((x) => x.id === planId);
  if (p) openDetail(p);
}

function closeModal(): void {
  const modal = $('modal');
  if (!modal || modal.style.display === 'none' || !modal.classList.contains('show')) return;
  modal.classList.remove('show');
  unlockBgScroll();
  modal.addEventListener('transitionend', function handler() {
    modal.style.display = 'none';
    modal.removeEventListener('transitionend', handler);
  });
}

// --- welcome (первый запуск) -----------------------------------------------

function showWelcomeModal(): void {
  const wm = $('welcomeModal');
  if (!wm) return;
  wm.style.display = 'block';
  requestAnimationFrame(() => wm.classList.add('show'));
  lockBgScroll();
  makeSheetDraggable(wm.querySelector('.modal-content'), closeWelcomeModal, wm.querySelector('.welcome'));
}

function closeWelcomeModal(): void {
  const wm = $('welcomeModal');
  if (!wm || !wm.classList.contains('show')) return;
  wm.classList.remove('show');
  unlockBgScroll();
  markWelcomeSeen();
  setTimeout(() => (wm.style.display = 'none'), 320);
}

// Инструкцию можно вызвать повторно (кнопкой «?» на категориях), поэтому закрытие
// биндим всегда, а авто-показ — только при первом заходе и если гайд включён.
function initWelcome(): void {
  const wm = $('welcomeModal');
  if (!wm) return;
  $('welcome-close-btn')?.addEventListener('click', closeWelcomeModal);
  wm.addEventListener('click', (e) => {
    if (e.target === wm) closeWelcomeModal();
  });
  if (config().welcome_enabled && !welcomeSeen()) {
    setTimeout(showWelcomeModal, 650);
  }
}

// Кнопка «?» в блоке категорий (Дизайн 2) — открывает инструкцию заново.
function renderCatHelp(): void {
  if (config().theme !== '2') return;
  const bar = $('rate-bar');
  if (!bar || bar.querySelector('.cat-help')) return;
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'cat-help';
  btn.setAttribute('aria-label', 'Как пользоваться витриной');
  // SVG вставляем напрямую — не зависим от набора иконок в бандле.
  btn.innerHTML =
    '<svg class="lucide" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
    'stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<circle cx="12" cy="12" r="10"/>' +
    '<path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></svg>';
  btn.addEventListener('click', showWelcomeModal);
  bar.appendChild(btn);
}

// --- события ---------------------------------------------------------------

function bindEvents(): void {
  $('product-grid')?.addEventListener('click', (ev) => {
    const el = ev.target as HTMLElement;
    const article = el.closest<HTMLElement>('.product-item');
    if (!article) return;
    const p = plans.find((x) => x.id === Number(article.dataset.id));
    if (!p) return;
    if (el.closest('[data-cart]')) {
      toggleCartItem(toCartItem(p));
      syncCartButtons();
      updateCartFooter();
    } else if (el.closest('[data-buy]')) {
      buyNow(p);
    } else if (el.closest('[data-details]')) {
      openDetail(p);
    }
  });

  $('category-container')?.addEventListener('click', (ev) => {
    const btn = (ev.target as HTMLElement).closest<HTMLElement>('.rate-item');
    if (!btn) return;
    activeCategory = btn.dataset.category || ALL;
    applyFilters();
    // Выбранную категорию подводим к центру ленты — видно соседние с обеих сторон.
    btn.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  });

  const search = document.querySelector<HTMLInputElement>('.search-bar input');
  search?.addEventListener('input', () => {
    searchQuery = search.value;
    applyFilters();
  });

  $('close-button')?.addEventListener('click', closeModal);
  $('modal')?.addEventListener('click', (e) => {
    if (e.target === $('modal')) closeModal();
  });
  $('add-to-cart-btn')?.addEventListener('click', () => {
    if (modalPlan) {
      toggleCartItem(toCartItem(modalPlan));
      syncCartButtons();
      updateCartFooter();
    }
    closeModal();
  });

  $('cart-footer')?.addEventListener('click', () => openCart());
}

// --- старт -----------------------------------------------------------------

function hidePreloader(): void {
  const pre = $('preloader');
  if (!pre) return;
  setTimeout(() => {
    pre.classList.add('hidden');
    setTimeout(() => (pre.style.display = 'none'), 500);
  }, 400);
}

function fatal(text: string, hint = ''): void {
  const grid = $('product-grid');
  if (!grid) return;
  const sub = hint ? `<div class="info-text" style="opacity:.6">${esc(hint)}</div>` : '';
  grid.innerHTML = `<div class="infotext"><div class="info-text">${esc(text)}</div>${sub}</div>`;
}

/** Объясняем причину, а не «что-то пошло не так»: по коду сразу видно, где чинить. */
function explain(e: unknown): [string, string] {
  const status = e instanceof ApiError ? e.status : -1;
  if (status === 401) {
    return ['Витрина не узнала вас', 'Откройте её заново из бота — подпись входа устарела.'];
  }
  if (status === 404) {
    return ['Магазин не найден', 'Возможно, он выключен в панели или ссылка ведёт на другой.'];
  }
  if (status >= 500) {
    return ['Магазин временно недоступен', `Ошибка сервера (${status}). Мы уже видим её в логах.`];
  }
  if (status === 0) {
    return ['Магазин не указан', 'Откройте витрину кнопкой из бота.'];
  }
  return ['Не удалось загрузить витрину', 'Проверьте связь и попробуйте ещё раз.'];
}

// Промо-баннер Дизайна 2 (мост). Пока текст здесь — при желании вынесем в панель
// (Оформление → промо-баннер). В Дизайне 1 не показываем.
const PROMO_BANNER = {
  title: 'Скидка на первую покупку',
  sub: 'Уже активна — выбери тариф',
  seal: '−50%',
};

function renderPromoBanner(): void {
  const host = $('banners');
  if (!host) return;
  if (config().theme !== '2') {
    host.innerHTML = '';
    return;
  }
  host.innerHTML = `<div class="promo-banner">
    <div class="promo-banner__body">
      <div class="promo-banner__title">${esc(PROMO_BANNER.title)}</div>
      <div class="promo-banner__sub">${esc(PROMO_BANNER.sub)}</div>
    </div>
    <div class="promo-banner__seal">${esc(PROMO_BANNER.seal)}</div>
  </div>`;
}

async function main(): Promise<void> {
  const t = tg();
  t?.ready();
  if (t) {
    enterFullscreen(); // на телефоне — полный экран, на десктопе — обычный expand
    hideBackButton(); // главная — нативная «Закрыть», а не «Назад» из корзины
  }
  initTheme();
  bindEvents();
  renderIcons(); // статичные иконки: поиск, корзина в футере, крестик модалки
  hidePreloader();

  if (!tenantSlug()) {
    fatal('Магазин не указан', 'Откройте витрину кнопкой из бота.');
    return;
  }
  // Конфиг раньше каталога: он решает, пускать ли с ПК. loadConfig не бросает —
  // на ПК без initData вернутся дефолты (desktop_enabled=false → блок).
  await loadConfig();
  applyMarketTheme(config().theme); // скин витрины из конфига (мост — свой)
  setLang(config().locale); // язык покупателя из конфига
  applyStaticI18n(); // фиксированные строки хрома в разметке
  if (isDesktopOrWeb() && !config().desktop_enabled) {
    showMobileOnly();
    return;
  }
  if (config().title) document.title = config().title;
  renderPromoBanner();
  try {
    plans = await api.catalog();
    renderGrid();
    renderCategories();
    renderCatHelp();
    updateCartFooter();
    initWelcome();
    // Баннеры персональные и не должны задерживать каталог — грузим следом.
    if (SHOW_BANNERS) void renderBanners(openPlanById);
  } catch (e) {
    console.error(e);
    fatal(...explain(e));
  }
}

void main();

// Возврат из корзины «назад» может восстановить страницу из bfcache без запуска
// main() — тогда BackButton так и остался бы видимым. Прячем и на pageshow.
window.addEventListener('pageshow', hideBackButton);
